import React, { useEffect, useState, useMemo } from "react";
import SummaryApi from "../common"; // Adjust path if needed

const CallDuration = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateFilter, setDateFilter] = useState("All");
  const [district, setDistrict] = useState("All");

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  // Fetch data once
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(SummaryApi.getCallDuration3min.url, {
          method: "GET",
          headers,
        });
        const result = await response.json();
        if (result.status) {
          setData(result.data);
          setFiltered(result.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Memoize districts to avoid unnecessary recalculations
  const districts = useMemo(() => {
    return [
      "All",
      ...Array.from(
        new Set(data.map((item) => item.district?.trim()).filter(Boolean))
      ),
    ];
  }, [data]);

  // Handle date filter preset changes
  const handleDateFilterChange = (value) => {
    setDateFilter(value);

    const today = new Date();
    let start = "";
    let end = "";

    switch (value) {
      case "Today":
        start = end = today.toISOString().split("T")[0];
        break;
      case "Yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        start = end = yesterday.toISOString().split("T")[0];
        break;
      case "Last 7 Days":
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 6);
        start = last7.toISOString().split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      case "Last 30 Days":
        const last30 = new Date(today);
        last30.setDate(today.getDate() - 29);
        start = last30.toISOString().split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      case "This Month":
        start = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        end = today.toISOString().split("T")[0];
        break;
      case "Last Month":
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        start = firstDayLastMonth.toISOString().split("T")[0];
        end = lastDayLastMonth.toISOString().split("T")[0];
        break;
      case "All":
        start = end = "";
        break;
      default:
        break;
    }

    setStartDate(start);
    setEndDate(end);
  };

  // Apply all filters
  useEffect(() => {
    let temp = [...data];

    // Filter by call status
    if (filter !== "All") {
      temp = temp.filter(
        (s) =>
          s.is_interested?.trim().toLowerCase() === filter.trim().toLowerCase()
      );
    }

    // Filter by district
    if (district !== "All") {
      temp = temp.filter(
        (s) =>
          s.district?.trim().toLowerCase() === district.trim().toLowerCase()
      );
    }

    // Filter by date
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1); // include the end day

      temp = temp.filter((s) => {
        const recordDate = new Date(s.created_date);
        return recordDate >= start && recordDate < end;
      });
    }

    // Filter by search
    if (search) {
      const lowerSearch = search.toLowerCase();
      temp = temp.filter(
        (s) =>
          s.student_name?.toLowerCase().includes(lowerSearch) ||
        s.team_lead_name?.toLowerCase().includes(lowerSearch) ||
        s.assigned_employee_name?.toLowerCase().includes(lowerSearch) ||
          s.clg_name?.toLowerCase().includes(lowerSearch) ||
          s.mobile_number1?.includes(search)
      );
    }

    setFiltered(temp);
    setCurrentPage(1);
  }, [search, filter, district, startDate, endDate, data]);

  // Pagination logic
  const totalRecords = filtered.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filtered.slice(indexOfFirstRecord, indexOfLastRecord);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="p-8 bg-slate-50 min-h-screen font-sans text-gray-800">
      <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-extrabold text-gray-900 mb-3 sm:mb-0">
            Students More Than 3 min Duration
          </h1>
        </div>

        <div className="flex justify-between">
          {/* Filter Buttons */}
          <div className="mb-6">
            <label className="font-bold">Select Call Status : </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg py-2 px-4 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-150 outline-none cursor-pointer"
            >
              {[
                "All",
                "Not Interested",
                "Interested",
                "Most Interested",
                "Call Back",
                "Wrong Number",
                "Switched Off",
              ].map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="font-bold">Select District : </label>
            {/* District Dropdown */}
            <select
              value={district}
              onChange={(e) => setDistrict(e.target.value)}
              className="mb-4 border border-gray-300 rounded-lg py-3 px-4 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-150 outline-none cursor-pointer min-w-[150px]"
            >
              {districts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-bold">Select Date : </label>

            {/* Date Filter Dropdown */}
            <select
              value={dateFilter}
              onChange={(e) => handleDateFilterChange(e.target.value)}
              className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-150 outline-none cursor-pointer min-w-[180px]"
            >
              <option value="All">All</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="Last 7 Days">Last 7 Days</option>
              <option value="Last 30 Days">Last 30 Days</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="Custom Range">Custom Range</option>
            </select>

            {/* Custom Range Inputs */}
            {dateFilter === "Custom Range" && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-150 outline-none cursor-pointer min-w-[150px]"
                  title="Start Date"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-150 outline-none cursor-pointer min-w-[150px]"
                  title="End Date"
                />
              </>
            )}
          </div>
        </div>

        {/* Search and Date Filters */}
        <div className="flex flex-wrap gap-4 mb-8 items-center">
          {/* Search bar */}
          <div className="relative flex-1 min-w-[180px]">
            <input
              type="text"
              placeholder="Search by student, employee, team lead ,college, or mobile number..."
              className="w-[50%] pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500 transition-all duration-150 outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-6a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>

          <button
            onClick={() => {
              setSearch("");
              setFilter("All");
              setDistrict("All");
              setStartDate("");
              setEndDate("");
              setDateFilter("All");
            }}
            className="px-5 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200 shadow-md outline-none focus:ring-2 focus:ring-red-200 focus:ring-offset-2"
          >
            Clear Filters
          </button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-blue-600 text-xl font-medium">
            Loading data...
            <span className="animate-pulse ml-2">...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-red-500 text-xl font-medium">
            No records found matching your criteria.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200">
            <table className="min-w-full text-sm text-left text-gray-700">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-4 py-3 border-r border-blue-500">SNO</th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    Enrollment Code
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    Student Name
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    District
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">City</th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    College Name
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">Mobile</th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    Call Status
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    Call Duration
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">
                    Enrolled Date
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">
              Employee Name
                  </th>
                  <th className="px-4 py-3 border-r border-blue-500">
                   Team Lead Name
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.map((item, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-gray-50" : ""}
                  >
                    <td className="px-4 py-2 border">
                      {indexOfFirstRecord + index + 1}
                    </td>
                    <td className="px-4 py-2 border">{item.enrollment_code}</td>
                    <td className="px-4 py-2 border">{item.student_name}</td>
                    <td className="px-4 py-2 border">{item.district}</td>
                    <td className="px-4 py-2 border">{item.city}</td>
                    <td className="px-4 py-2 border">{item.clg_name}</td>
                    <td className="px-4 py-2 border">{item.mobile_number1}</td>
                    <td className="px-4 py-2 border">{item.is_interested}</td>
                    <td className="px-4 py-2 border">
                      {item.call_duration?.split(".")[0]}
                    </td>

                    <td className="px-4 py-2 border">{item.created_date}</td>
                    <td className="px-4 py-2 border">{item.assigned_employee_name}</td>
                    <td className="px-4 py-2 border">{item.team_lead_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center mt-4">
            <div>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-lg mr-2 disabled:opacity-50"
              >
                Prev
              </button>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div>
              Page {currentPage} of {totalPages}
            </div>
            <div>
              Records per page:{" "}
              <select
                value={recordsPerPage}
                onChange={handleRecordsPerPageChange}
                className="border border-gray-300 rounded-lg px-2 py-1"
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallDuration;
